// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import {
  AppIcon,
  AssetLogo,
  classNames,
  defineComponent,
  isBuildDevelopment,
  ShowInBuildDevelopment,
  Table,
} from '@shared/ui-kit';
import { ShowInAppClient } from '@shared/ui-kit/utils/app-utils';
import { isClientApp } from '@shared/environment';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { useBanksList } from './use-banks-list';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useState } from 'react';
import type { TFunction } from 'i18next';
import {
  AccountStatus,
  AdvisoryBankProductType,
  type AdvisoryBank,
  type AdvisoryBankAccount,
  type AdvisoryBankCard,
  type AdvisoryBankLoan,
  type AdvisoryBankPortfolio,
  type AdvisoryBankProduct,
} from '@shared/data-access/types/brokerage-types';
import { ObjectRowTags } from '@shared/advisory/aggregation/components/tags';
import { TagObjectType } from '@shared/data-access/interfaces/redux/tags-management-state-types';
import { getProductTypeLabel } from '@shared/advisory/aggregation/utils/get-product-type-label';
import styles from './banks-list.module.css';
import { InstitutionSyncStatus } from './institution-sync-status';
import { MaskIban } from './mask-iban';
import { SyncAccountsDrawer } from './sync-accounts-drawer';
// eslint-disable-next-line @nx/enforce-module-boundaries -- bank details UI lives in @client/banks
import {
  BankAccountUpdateDrawer,
  BankInstitutionUpdateDrawer,
} from '@client/banks/components/bank-details';
import {
  isManualCitecInstitution,
  isNuvaraInversisInstitution,
} from '@shared/data-access/utils';
import { IconButton } from '@shared/ui-kit/components/icon-button';

type ProductRowSlotProps<T extends AdvisoryBankProduct> = {
  product: T;
  institutionId: string;
  currency: string;
  onAccountClick?: (institution: string, iban: string) => void;
  onEditAccount?: () => void;
  showAccountEdit?: boolean;
  onUnshareAccount?: () => void;
  showAccountUnshare?: boolean;
  isLastRow: boolean;
  t: TFunction;
};

const accountStatusColorMap: Record<
  AccountStatus,
  'success' | 'warning' | 'error' | 'default' | 'info'
> = {
  [AccountStatus.Active]: 'success',
  [AccountStatus.Pending]: 'info',
  [AccountStatus.InactiveDocsPending]: 'warning',
  [AccountStatus.InactiveDocsSent]: 'default',
  [AccountStatus.CancellationPending]: 'warning',
  [AccountStatus.Cancelled]: 'error',
};

export const BanksList = defineComponent({
  displayName: 'BanksList',
  useSetup: useBanksList,
  Slots: {
    AccountRow: (props: ProductRowSlotProps<AdvisoryBankAccount>) => (
      <ListItem
        disablePadding
        secondaryAction={
          props.showAccountEdit || props.showAccountUnshare ? (
            <div className="flex items-center gap-1">
              {props.showAccountEdit ? (
                <Tooltip title={<Translation i18nKey="common.edit" ns="common" />}>
                  <IconButton
                    icon="Pen"
                    variant="icon"
                    color="dark"
                    size="lg"
                    aria-label="Edit account"
                    data-testid={`bank-account-edit-${props.product.iban}`}
                    onClick={e => {
                      e.stopPropagation();
                      props.onEditAccount?.();
                    }}
                  />
                </Tooltip>
              ) : null}
              {props.showAccountUnshare ? (
                <Tooltip
                  title={<Translation i18nKey="common.unshare" ns="common" />}
                >
                  <IconButton
                    icon="Trashcan"
                    variant="icon"
                    color="dark"
                    size="lg"
                    aria-label={props.t('common.unshare', { ns: 'common' })}
                    data-testid={`bank-account-unshare-${props.product.iban}`}
                    onClick={e => {
                      e.stopPropagation();
                      props.onUnshareAccount?.();
                    }}
                  />
                </Tooltip>
              ) : null}
            </div>
          ) : undefined
        }
      >
        <ListItemButton
          className={classNames(
            styles['row-button'],
            props.isLastRow && styles['row-button-last'],
          )}
          onClick={() =>
            props.onAccountClick?.(props.institutionId, props.product.iban!)
          }
        >
          <ListItemIcon>
            <div className={styles['icon-wrapper']}>
              <AppIcon icon="Wallet" color="inherit" />
            </div>
          </ListItemIcon>

          <ListItemText
            primary={
              <div className="flex items-center gap-4">
                <Typography variant="h6" className={styles['primary-name']}>
                  {props.product.name}
                </Typography>

                {props.product.account_status &&
                  isNuvaraInversisInstitution(props.institutionId) && (
                    <Chip
                      label={
                        <Translation
                          i18nKey={`aggregator.account-status.${props.product.account_status}`}
                          ns="aggregator"
                          defaults={props.product.account_status}
                        />
                      }
                      size="small"
                      color={accountStatusColorMap[props.product.account_status]}
                      className="min-w-20"
                    />
                  )}
              </div>
            }
            secondary={getProductTypeLabel(
              AdvisoryBankProductType.Account,
              props.product.type,
              props.t,
            )}
            secondaryTypographyProps={{
              variant: 'body2',
              className: styles['secondary-type'],
            }}
          />

          <ListItemText
            primary={<Translation i18nKey="common.account-number" ns="common" />}
            primaryTypographyProps={{
              className: styles['account-number-label'],
            }}
            secondaryTypographyProps={{
              component: 'div',
              className: classNames(
                styles['account-number-value'],
                '!money-display',
              ),
            }}
            secondary={
              <MaskIban iban={props.product.iban} format="first-and-last" />
            }
          />

          <ObjectRowTags
            objectType={TagObjectType.Account}
            objectId={props.product.iban ?? ''}
          />

          <ListItemText
            primaryTypographyProps={{
              className: styles['balance-label'],
            }}
            secondaryTypographyProps={{
              className: styles['balance-value'],
            }}
            primary={<Translation i18nKey="common.balance" ns="common" />}
            secondary={
              <Table.Data
                value={props.product.balance}
                format="currency"
                safeValue="-"
                currency={props.currency}
              />
            }
          />
        </ListItemButton>
      </ListItem>
    ),
    CardRow: (props: ProductRowSlotProps<AdvisoryBankCard>) => (
      <ListItem disablePadding>
        <ListItemButton
          onClick={() =>
            props.onAccountClick?.(props.institutionId, props.product.iban!)
          }
          className={classNames(
            styles['row-button'],
            props.isLastRow && styles['row-button-last'],
          )}
        >
          <ListItemIcon>
            <div className={styles['icon-wrapper']}>
              <AppIcon icon="CreditCard" color="inherit" />
            </div>
          </ListItemIcon>

          <ListItemText
            primaryTypographyProps={{
              variant: 'h6',
              className: styles['primary-name'],
            }}
            primary={props.product.name}
            secondary={getProductTypeLabel(
              AdvisoryBankProductType.Card,
              props.product.type,
              props.t,
            )}
            secondaryTypographyProps={{
              variant: 'body2',
              className: styles['secondary-type'],
            }}
          />

          <ListItemText
            primary={<Translation i18nKey="common.account-number" ns="common" />}
            primaryTypographyProps={{
              className: styles['account-number-label'],
            }}
            secondaryTypographyProps={{
              component: 'div',
              className: classNames(
                styles['account-number-value'],
                '!money-display',
              ),
            }}
            secondary={
              <MaskIban iban={props.product.iban} format="first-and-last" />
            }
          />

          <ObjectRowTags
            objectType={TagObjectType.Account}
            objectId={props.product.iban ?? ''}
          />

          <ListItemText
            primaryTypographyProps={{
              className: styles['balance-label'],
            }}
            secondaryTypographyProps={{
              className: styles['balance-value'],
            }}
            primary={<Translation i18nKey="common.balance" ns="common" />}
            secondary={
              <Table.Data
                value={props.product.balance}
                format="currency"
                safeValue="-"
                currency={props.currency}
              />
            }
          />
        </ListItemButton>
      </ListItem>
    ),
    LoanRow: (props: ProductRowSlotProps<AdvisoryBankLoan>) => (
      <ListItem disablePadding>
        <ListItemButton
          onClick={() =>
            props.onAccountClick?.(props.institutionId, props.product.iban!)
          }
          className={classNames(
            styles['row-button'],
            props.isLastRow && styles['row-button-last'],
          )}
        >
          <ListItemIcon>
            <div className={styles['icon-wrapper']}>
              <AppIcon icon="HandCoins" color="inherit" />
            </div>
          </ListItemIcon>

          <ListItemText
            primaryTypographyProps={{
              variant: 'h6',
              className: styles['primary-name'],
            }}
            primary={props.product.name}
            secondary={getProductTypeLabel(
              AdvisoryBankProductType.Loan,
              props.product.type,
              props.t,
            )}
            secondaryTypographyProps={{
              variant: 'body2',
              className: styles['secondary-type'],
            }}
          />

          <ListItemText
            primary={<Translation i18nKey="common.account-number" ns="common" />}
            primaryTypographyProps={{
              className: styles['account-number-label'],
            }}
            secondaryTypographyProps={{
              component: 'div',
              className: classNames(
                styles['account-number-value'],
                '!money-display',
              ),
            }}
            secondary={
              <MaskIban iban={props.product.iban} format="first-and-last" />
            }
          />

          <ObjectRowTags
            objectType={TagObjectType.Account}
            objectId={props.product.iban ?? ''}
          />

          <ListItemText
            primaryTypographyProps={{
              className: styles['balance-label'],
            }}
            secondaryTypographyProps={{
              className: styles['balance-value'],
            }}
            primary={<Translation i18nKey="common.balance" ns="common" />}
            secondary={
              <Table.Data
                value={props.product.balance}
                format="currency"
                safeValue="-"
                currency={props.currency}
              />
            }
          />
        </ListItemButton>
      </ListItem>
    ),
    PortfolioRow: (props: ProductRowSlotProps<AdvisoryBankPortfolio>) => (
      <ListItem disablePadding>
        <ListItemButton
          onClick={() =>
            props.onAccountClick?.(props.institutionId, props.product.iban!)
          }
          className={classNames(
            styles['row-button'],
            props.isLastRow && styles['row-button-last'],
          )}
        >
          <ListItemIcon>
            <div className={styles['icon-wrapper']}>
              <AppIcon icon="TrendingUp" color="inherit" />
            </div>
          </ListItemIcon>

          <ListItemText
            primaryTypographyProps={{
              variant: 'h6',
              className: styles['primary-name'],
            }}
            primary={props.product.name}
            secondary={getProductTypeLabel(
              AdvisoryBankProductType.Portfolio,
              props.product.type,
              props.t,
            )}
            secondaryTypographyProps={{
              variant: 'body2',
              className: styles['secondary-type'],
            }}
          />

          <ListItemText
            primary={<Translation i18nKey="common.account-number" ns="common" />}
            primaryTypographyProps={{
              className: styles['account-number-label'],
            }}
            secondaryTypographyProps={{
              component: 'div',
              className: classNames(
                styles['account-number-value'],
                '!money-display',
              ),
            }}
            secondary={
              <MaskIban iban={props.product.iban} format="first-and-last" />
            }
          />

          <ObjectRowTags
            objectType={TagObjectType.Account}
            objectId={props.product.iban ?? ''}
          />

          <ListItemText
            primaryTypographyProps={{
              className: styles['balance-label'],
            }}
            secondaryTypographyProps={{
              className: styles['balance-value'],
            }}
            primary={<Translation i18nKey="common.balance" ns="common" />}
            secondary={
              <Table.Data
                value={props.product.balance}
                format="currency"
                safeValue="-"
                currency={props.currency}
              />
            }
          />
        </ListItemButton>
      </ListItem>
    ),
  },
  View: ({
    banks,
    t,
    currency,
    isExpanded,
    getToggleHandler,
    onAccountClick,
    onSyncClick,
    onRefreshBank,
    onRemoveBank,
    onUnshareAccount,
    Slots,
  }) => {
    const [selectedInstitution, setSelectedInstitution] =
      useState<AdvisoryBank | null>(null);
    const [detailsInstitution, setDetailsInstitution] =
      useState<AdvisoryBank | null>(null);
    const [editingAccount, setEditingAccount] = useState<{
      institution: AdvisoryBank;
      account: AdvisoryBankAccount;
    } | null>(null);

    return (
      <div className={styles.container}>
        {Array.from(banks, institution => (
          <Accordion
            key={institution.id}
            id={institution.id}
            expanded={isExpanded(institution.id)}
            onChange={getToggleHandler(institution.id)}
            classes={{
              root: styles['accordion-root'],
              expanded: styles['accordion-expanded'],
            }}
            disabled={institution.products.length === 0}
          >
            <AccordionSummary
              expandIcon={<AppIcon icon="ArrowDown" color="inherit" />}
              classes={{
                root: styles['summary-root'],
                content: styles['summary-content'],
                expandIconWrapper: styles['summary-expand-icon'],
              }}
              sx={{
                '& .MuiAccordionSummary-expandIconWrapper': {
                  color: 'var(--sidebarDisabled)',
                },
              }}
            >
              <header className={styles.header}>
                <div className={styles['logo-box']}>
                  {institution?.logo_url ? (
                    <AssetLogo
                      logoUrl={institution?.logo_url}
                      className={styles['logo-img']}
                    />
                  ) : (
                    <AppIcon icon="Building" color="disabled" size="lg" />
                  )}
                </div>

                <div className={styles['header-text']}>
                  <div className={styles['institution-name-row']}>
                    <Typography variant="h6" className={styles['institution-name']}>
                      {institution?.name}
                    </Typography>

                    {institution.is_oneshot ? (
                      <Tooltip
                        title={
                          <Translation
                            i18nKey="aggregator.one-shot-message"
                            ns="aggregator"
                          />
                        }
                      >
                        <Chip
                          size="small"
                          color="warning"
                          className="min-w-20"
                          label={
                            <Translation
                              i18nKey="aggregator.one-shot"
                              ns="aggregator"
                            />
                          }
                          data-testid="bank-one-shot-institution-chip"
                        />
                      </Tooltip>
                    ) : null}

                    {institution.is_business ? (
                      <Chip
                        size="small"
                        color="info"
                        className="min-w-20"
                        label={
                          <Translation
                            i18nKey="aggregator.business"
                            ns="aggregator"
                          />
                        }
                        data-testid="bank-business-institution-chip"
                      />
                    ) : null}

                    {isManualCitecInstitution(institution) ? (
                      <Tooltip
                        title={
                          <Translation
                            i18nKey="aggregator.manual-bank-message"
                            ns="aggregator"
                          />
                        }
                      >
                        <Chip
                          size="small"
                          color="warning"
                          className="min-w-20"
                          label={
                            <Translation
                              i18nKey="aggregator.create-bank.accounts.manual-tag"
                              ns="aggregator"
                            />
                          }
                          data-testid="bank-manual-institution-chip"
                        />
                      </Tooltip>
                    ) : null}
                  </div>

                  <Typography variant="body2" className={styles['account-count']}>
                    <Translation
                      i18nKey="aggregator.account"
                      ns="aggregator"
                      count={institution.products.length}
                    />
                  </Typography>
                </div>
              </header>

              <div className={styles.actions}>
                {!isNuvaraInversisInstitution(institution) &&
                  !isManualCitecInstitution(institution) && (
                    <InstitutionSyncStatus
                      institution={institution}
                      onSelectInstitution={setSelectedInstitution}
                      onSyncBank={onRefreshBank}
                    />
                  )}

                <span className={styles['total-amount']}>
                  <Table.Data
                    value={institution.balance}
                    format="currency"
                    currency={currency}
                    safeValue="-"
                  />
                </span>

                <div className="flex size-5 items-center justify-center">
                  <ShowInAppClient>
                    <ShowInBuildDevelopment>
                      {isManualCitecInstitution(institution) ? (
                        <Tooltip title={t('common.edit', { ns: 'common' })}>
                          <IconButton
                            icon="Pen"
                            variant="icon"
                            color="dark"
                            size="lg"
                            onClick={e => {
                              e.stopPropagation();
                              setDetailsInstitution(institution);
                            }}
                            data-testid="bank-institution-details-button"
                          />
                        </Tooltip>
                      ) : null}
                    </ShowInBuildDevelopment>
                  </ShowInAppClient>

                  {!isNuvaraInversisInstitution(institution) &&
                  !isManualCitecInstitution(institution) &&
                  onRemoveBank ? (
                    <Tooltip title={t('common.remove', { ns: 'common' })}>
                      <IconButton
                        icon="Trashcan"
                        variant="icon"
                        color="dark"
                        size="lg"
                        aria-label={t('aggregator.disconnect-account', {
                          ns: 'aggregator',
                        })}
                        data-testid={`bank-institution-remove-${institution.id}`}
                        onClick={e => {
                          e.stopPropagation();
                          onRemoveBank(institution.id, institution.name);
                        }}
                      />
                    </Tooltip>
                  ) : null}
                </div>
              </div>
            </AccordionSummary>

            <AccordionDetails
              classes={{
                root: styles['details-root'],
              }}
            >
              <List disablePadding className={styles.list}>
                {Array.from(institution.products).map((account, index, items) => {
                  const isManualBank = isManualCitecInstitution(institution);
                  const canEditManualAccount =
                    isClientApp &&
                    isBuildDevelopment &&
                    isManualBank &&
                    account.product_type === AdvisoryBankProductType.Account;

                  const canUnshareAccount =
                    !!onUnshareAccount &&
                    !!account.iban &&
                    account.product_type === AdvisoryBankProductType.Account &&
                    !isManualBank &&
                    !isNuvaraInversisInstitution(institution);

                  const rowProps: ProductRowSlotProps<any> = {
                    product: account,
                    institutionId: institution.id,
                    currency,
                    onAccountClick,
                    showAccountEdit: canEditManualAccount,
                    onEditAccount: canEditManualAccount
                      ? () => {
                          setEditingAccount({
                            institution,
                            account: account as AdvisoryBankAccount,
                          });
                        }
                      : undefined,
                    showAccountUnshare: canUnshareAccount,
                    onUnshareAccount: canUnshareAccount
                      ? () => {
                          onUnshareAccount({
                            institutionId: institution.id,
                            institutionName: institution.name,
                            iban: account.iban,
                            name: account.name ?? undefined,
                          });
                        }
                      : undefined,
                    isLastRow: index === items.length - 1,
                    t,
                  };

                  return (
                    <Fragment key={`${institution.id}-${index}`}>
                      {isExpanded(institution.id) && <Divider />}

                      {account.product_type === AdvisoryBankProductType.Card && (
                        <Slots.CardRow {...rowProps} />
                      )}

                      {account.product_type === AdvisoryBankProductType.Loan && (
                        <Slots.LoanRow {...rowProps} />
                      )}

                      {account.product_type ===
                        AdvisoryBankProductType.Portfolio && (
                        <Slots.PortfolioRow {...rowProps} />
                      )}

                      {account.product_type === AdvisoryBankProductType.Account && (
                        <Slots.AccountRow {...rowProps} />
                      )}
                    </Fragment>
                  );
                })}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}

        <SyncAccountsDrawer
          open={!!selectedInstitution}
          institution={selectedInstitution}
          onClose={() => setSelectedInstitution(null)}
          onRemoveBank={onRemoveBank}
          onSync={() => {
            if (selectedInstitution) {
              onSyncClick?.(selectedInstitution.id);
              setSelectedInstitution(null);
            }
          }}
        />

        <ShowInAppClient>
          <ShowInBuildDevelopment>
            <BankInstitutionUpdateDrawer
              open={!!detailsInstitution}
              institution={detailsInstitution}
              onClose={() => setDetailsInstitution(null)}
              onDelete={
                onRemoveBank
                  ? () => {
                      if (!detailsInstitution) {
                        return;
                      }

                      onRemoveBank(detailsInstitution.id, detailsInstitution.name);
                      setDetailsInstitution(null);
                    }
                  : undefined
              }
            />

            <BankAccountUpdateDrawer
              open={!!editingAccount}
              institution={editingAccount?.institution ?? null}
              account={editingAccount?.account ?? null}
              onClose={() => setEditingAccount(null)}
            />
          </ShowInBuildDevelopment>
        </ShowInAppClient>
      </div>
    );
  },
});
